import {
    ReadonlyMat4,
    ReadonlyVec2,
    ReadonlyVec3,
    mat4,
    vec2,
    vec3,
} from 'gl-matrix';
import { PanZoom } from '../../view/PanZoom';
import { View } from '../../view/View';
import { PlaneView } from '../../view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../EventHandlerResult';
import { ModifierKeys } from '../ModifierKeys';
import {
    MOUSE_BUTTON_LEFT,
    MOUSE_BUTTON_MIDDLE,
    MOUSE_BUTTON_RIGHT,
    MOUSE_BUTTONS_LEFT_MASK,
    MOUSE_BUTTONS_MIDDLE_MASK,
    MOUSE_BUTTONS_RIGHT_MASK,
} from '../MouseButton';
import { ToolAction } from '../ToolAction';

/**
 * State to store the pan action.
 *
 * {@link PanAction} starts with {@link PanAction.onMousePress} and ends with {@link PanAction.onMouseRelease}.\
 * In between the start and end events the {@link PanActionEventState} is used to store the {@link PanAction.activeEventState state} of the {@link PanAction}.
 */
class PanActionEventState {
    /**
     * The {@link ReadonlyVec3 PatientSpace coordinates} of the mouse when the {@link PanAction} started.
     */
    public readonly startPatient: ReadonlyVec3;

    /**
     * {@link ReadonlyMat4 transformationMatrix} that the {@link View} had when the {@link PanAction} started.\
     * This is a copy of the original {@link ReadonlyMat4 matrix} as the original is manipulated during the {@link PanAction}.
     */
    public readonly transformationMatrix: ReadonlyMat4;

    /**
     * The {@link PanZoom.translation translation} {@link ReadonlyVec3 coordinates} when the {@link PanAction} started.
     */
    public readonly panCenter: ReadonlyVec3;

    public constructor(
        public readonly view: PlaneView,
        startPatient: ReadonlyVec3,
        transformationMatrix: ReadonlyMat4,
        panCenter: ReadonlyVec3,
        public readonly startMouseCoordinates: ReadonlyVec2 // where the mouse event started
    ) {
        this.startPatient = startPatient;
        this.transformationMatrix = transformationMatrix;
        this.panCenter = panCenter;
    }

    /**
     * Method to get the updated {@link PanZoom._translation translation} of the {@link PanAction}.
     * @param coordinates The mouse position in client pixel coordinates at the time of the event.\
     * These are converted into viewRelativeCoordinates in between (-1, 1) and then further into patientSpaceCoordinates.
     * @returns The new {@link ReadonlyVec3 PatientSpaceCoordinates} that are to be assigned to the {@link PanZoom._translation PanZoom.translation}.
     */
    public getUpdatedCenter(coordinates: ReadonlyVec2): ReadonlyVec3 {
        const viewCoordinates = this.view.clientToViewCoordinates(coordinates);
        const patientCoordinates = vec3.transformMat4(
            vec3.create(),
            vec3.fromValues(viewCoordinates[0], viewCoordinates[1], 0),
            this.transformationMatrix
        );
        const delta = vec3.subtract(
            vec3.create(),
            patientCoordinates,
            this.startPatient
        ); //delta patient
        return vec3.subtract(vec3.create(), this.panCenter, delta);
    }
}

/**
 * {@link PanAction} provides functionality to modify the {@link PanZoom._translation translation} of a {@link PanZoom}-object.
 */
export class PanAction extends ToolAction {
    /**
     * The {@link PanZoom}-object that this {@link PanAction} will modify.
     */
    private readonly panZoom: PanZoom;

    /**
     * Mouse buttons to which this {@link PanAction} should react to.
     */
    private readonly mouseButtons: number[];

    /**
     * {@link ModifierKeys} that need to be active to use this {@link PanAction}.
     */
    private readonly modifierKeys: ModifierKeys;

    /**
     * {@link PanAction} constructor, which takes the {@link PanZoom}-object, {@link mouseButton} and optional {@link ModifierKeys} as parameters.
     * @param panZoom The {@link PanZoom}-object that is to be altered by this {@link PanAction}.
     * @param mouseButton The mouseButton (or array of buttons) this {@link PanAction} uses.
     * @param modifierKeys {@link ModifierKeys} that need to be active to use this {@link PanAction}. Defaults to {@link ModifierKeys.NONE NONE}.
     */
    public constructor(
        panZoom: PanZoom,
        mouseButton: number | number[],
        modifierKeys: ModifierKeys = ModifierKeys.NONE
    ) {
        super();
        this.panZoom = panZoom;
        this.mouseButtons = Array.isArray(mouseButton)
            ? mouseButton
            : [mouseButton];
        this.modifierKeys = modifierKeys;
    }

    private _startState: PanActionEventState | undefined;

    /** Last mouse coordinates seen during an active pan.
     *  Used by {@link refreshStartState} to re-anchor the pan
     *  at the current cursor position instead of the original
     *  start position. */
    private _lastMouseCoordinates: ReadonlyVec2 | undefined;

    /** Whether this pan action was previously active before being
     *  cancelled (e.g. mouse left viewport). Only when true should
     *  the auto-restart in onMouseMove re-initiate a pan -- this
     *  prevents spurious pan starts when the button is held by
     *  something else (e.g. crosshair drag). */
    private _wasPreviouslyActive = false;

    /** The view in which the drag started — all coordinate
     *  conversions and pan operations must use this view to avoid
     *  cross-view projection mismatches when the mouse crosses
     *  into another viewport during a drag. Survives cancel(). */
    private _startView: PlaneView | undefined;

    /**
     * Whether this pan action is currently active (has a start state).
     */
    public get isActive(): boolean {
        return this._startState !== undefined;
    }

    /**
     * Maps a mouse button index to the corresponding
     * {@link MouseEvent.buttons} bitmask value.
     * button 0 (left) -> 1, button 1 (middle) -> 4, button 2 (right) -> 2.
     */
    private static buttonToBitmask(button: number): number {
        switch (button) {
            case MOUSE_BUTTON_LEFT:
                return MOUSE_BUTTONS_LEFT_MASK;
            case MOUSE_BUTTON_MIDDLE:
                return MOUSE_BUTTONS_MIDDLE_MASK;
            case MOUSE_BUTTON_RIGHT:
                return MOUSE_BUTTONS_RIGHT_MASK;
            default:
                return 0;
        }
    }

    /**
     * Creates a new start state from the given coordinates and view.
     * Shared initialization logic used by both {@link onMousePress}
     * and {@link forceStart}.
     */
    private initStartState(coordinates: ReadonlyVec2, view: PlaneView): void {
        const transformationMatrix = mat4.clone(view.transformationMatrix);
        const viewCoordinates = view.clientToViewCoordinates(coordinates);
        this._startState = new PanActionEventState(
            view,
            vec3.transformMat4(
                vec3.create(),
                vec3.fromValues(viewCoordinates[0], viewCoordinates[1], 0),
                transformationMatrix
            ),
            transformationMatrix,
            vec3.clone(view.position),
            coordinates
        );
        this._startView = view;
        this._lastMouseCoordinates = coordinates;
    }

    /**
     * MousePress event that was caught by the canvas and propagated by {@link ToolManager} to this {@link PanAction}.\
     * This sets the {@link PanAction.activeEventState activeEventState} and starts a {@link PanAction} if the event is successfully handled.
     * @param button The button that triggered the event
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.START_ACTION} if
     * 1. the {@link button} equals {@link PanAction.mouseButton},
     * 2. the {@link modifierKeys} equal {@link PanAction.modifierKeys} and
     * 3. the {@link view} is an instanceof {@link PlaneView},
     *
     * in which case the event was successfully handled and {@link PanAction} was started.
     *
     * If any of the conditions is not met, the event is not handled and {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} is returned.
     */
    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: View
    ): EventHandlerResult {
        if (
            this.mouseButtons.includes(button) &&
            modifierKeys.equals(this.modifierKeys) &&
            view instanceof PlaneView
        ) {
            if (!this._startState) {
                this.initStartState(coordinates, view);
                return new EventHandlerResult(
                    EventHandlerResultType.EVENT_HANDLED,
                    true
                );
            }
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * Starts a pan action unconditionally, without checking button
     * or modifier keys. Used by key-pan triggers (Space+drag,
     * Cmd+drag) where the activation condition is external.
     * @param coordinates The mouse position in client pixel coordinates
     * @param view The {@link PlaneView} on which to start panning
     */
    public forceStart(coordinates: ReadonlyVec2, view: PlaneView): void {
        this.initStartState(coordinates, view);
    }

    /**
     * MouseMove event that was caught by the canvas and propagated by {@link ToolManager} to this {@link PanAction}.\
     * This updates the {@link panZoom}-object's {@link PanZoom._translation translation} coordinates if the event is successfully handled.
     *
     * If the pan was cancelled (e.g. mouse left the viewport) but
     * the button is still held, auto-restarts the pan from the
     * current position so panning resumes seamlessly on re-enter.
     *
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param buttons The {@link MouseEvent.buttons} bitmask
     * @param view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_HANDLED EVENT_HANDLED} if
     * the {@link activeEventState} is defined, in which case the event was successfully handled.\
     * {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} if the event was not handled.
     */
    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        _view: View
    ): EventHandlerResult {
        // Auto-restart: if pan was cancelled but the button is
        // still held, seamlessly resume from current position.
        // Always use the original start view to avoid jumping
        // when the mouse crosses into another viewport.
        if (
            !this._startState &&
            this._wasPreviouslyActive &&
            this._startView &&
            this.mouseButtons.some(
                (b) => (buttons & PanAction.buttonToBitmask(b)) !== 0
            )
        ) {
            this.initStartState(coordinates, this._startView);
            this._wasPreviouslyActive = false;
        }

        if (this._startState) {
            this._lastMouseCoordinates = coordinates;
            this._startState.view.panTo(
                this._startState.getUpdatedCenter(coordinates)
            );

            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * MouseRelease event that was caught by the canvas and propagated by {@link ToolManager} to this {@link PanAction}.\
     * If this {@link PanAction} is active, in which case it has a {@link PanAction.activeEventState activeEventState},\
     * the {@link panZoom}-object's {@link PanZoom._translation translation} coordinates are updated, the {@link PanAction.activeEventState activeEventState} is erased,
     * this {@link PanAction} is finished and {@link EventHandlerResult} of type {@link EventHandlerResultType.FINISH_ACTION FINISH_ACTION} is returned.
     *
     * If {@link PanAction.activeEventState activeEventState} is undefined {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} is returned.
     * @param button The button that triggered the event
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.FINISH_ACTION FINISH_ACTION} if {@link PanAction.activeEventState activeEventState} is defined,\
     * {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} otherwise.
     */
    public onMouseRelease(
        _button: number,
        coordinates: ReadonlyVec2
    ): EventHandlerResult {
        if (!this.mouseButtons.includes(_button)) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        let ret = new EventHandlerResult(
            EventHandlerResultType.EVENT_NOT_HANDLED
        );
        if (
            this._startState &&
            !vec2.equals(coordinates, this._startState.startMouseCoordinates)
        ) {
            ret = new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
            this._startState.view.suppressNextClick();
        }
        this._startState = undefined;
        this._startView = undefined;
        this._lastMouseCoordinates = undefined;
        this._wasPreviouslyActive = false;
        return ret;
    }

    /**
     * Re-anchor the pan start state at the last known mouse
     * position using the view's current transformation matrix.
     * Call after external changes to the view (e.g. slice
     * scroll) that invalidate the stored transformation
     * matrix. Uses the last mouse coordinates (not the
     * original pan-start coordinates) so that the accumulated
     * pan offset is not re-applied, which would cause a jump.
     */
    public refreshStartState(): void {
        if (this._startState && this._lastMouseCoordinates) {
            this.initStartState(
                this._lastMouseCoordinates,
                this._startState.view
            );
        }
    }

    /**
     * Unconditionally cancels the current pan action.
     * Used when the mouse leaves the viewport to prevent stuck pan state.
     */
    public cancel(): void {
        if (this._startState) {
            this._wasPreviouslyActive = true;
        }
        this._startState = undefined;
        this._lastMouseCoordinates = undefined;
    }
}
